/**
 * binaryhttp.service.ts
 *
 * Author: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */
import bluebird from "bluebird";
import { deserializeStream, Document } from "bson";

import { HttpError, AbortError, errorFor } from "../models/error";

export interface BinaryHttpService {
  get<S extends Record<string, string>>(path: string, params?: S): Promise<ArrayBuffer>,
  getBson<T extends Document, S extends Record<string, string | string[] | undefined>>(path: string, params?: S, method?: string): Promise<Array<T>>,
  getBsonStream<T extends Document, S extends Record<string, string | string[] | undefined>>(path: string, params?: S, method?: string): AsyncGenerator<T, void, void>,
  post<T, S extends Partial<Record<keyof S, string | undefined>> = object>(path: string, data: T, params?: S): Promise<ArrayBuffer>,
  put<T>(path: string, data: T): Promise<ArrayBuffer>,
  postBuf(path: string, data: ArrayBuffer, token?: string): Promise<ArrayBuffer>,
  postBufJSON<T>(path: string, data: ArrayBuffer, token?: string): Promise<T>
  postFileXHR(path: string, file: File, headers: { [name: string]: string } | null,
    progress: (uploaded: number, total: number) => void,
    xhrHook?: (xhr: XMLHttpRequest) => void): Promise<Uint8Array>,
  //postFile(path: string, data: File, abortSignal: AbortSignal): Promise<any>,

  setTokenFactory(factory: (path: string) => Promise<string | null>): void
  setDomain(domain: string): void,
  currentDomain(): string

}


class BinaryHTTPService implements BinaryHttpService {
  tokenFactory: (path: string) => Promise<string | null>;

  private domain: string;

  constructor() {
    this.domain = "";
    this.tokenFactory = (): Promise<string> => bluebird.resolve("");
  }

  setDomain(domain: string): void {
    this.domain = domain;
  }
  currentDomain(): string {
    return this.domain;
  }

  async get<S extends Record<string, string>>(path: string, params?: S): Promise<ArrayBuffer> {

    if (params) {
      const queryParams = new URLSearchParams();
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          queryParams.append(key, params[key]);
        }
      }
      path = path + "?" + queryParams.toString();
    }

    const headers = new Headers({ "X-Requested-With": "XMLHttpRequest" });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }

    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }

    const response = await fetch(path, {
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers
    });

    if (!response.ok) {
      const body = await response.json();
      console.error("Couldn't fetch:", body, response);
      throw errorFor(body, response.status);
    }

    return await response.arrayBuffer();
  }

  async getBson<T extends Document, S extends Record<string, string | string[] | undefined>>(path: string, params?: S, method?: string): Promise<Array<T>> {

    if (params) {
      path = path + "?" + this.buildQueryParams(params);
    }

    const headers = new Headers({ "X-Requested-With": "XMLHttpRequest" });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }

    const response = await fetch(path, {
      method: method || "GET",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers
    });

    if (!response.ok) {
      const body = await response.json();
      console.error("Couldn't fetch:", body, response);
      throw errorFor(body, response.status);
    }

    /*let count: string | null | number = response.headers.get("Doc-Count");
    if (!count) {
      throw new HttpError("No doc count present on response", response.status);
    }
    count = parseInt(count, 10);*/

    const docs: Array<T> = [];
    const buf = await response.arrayBuffer();
    const view = new DataView(buf);
    let offset = 0;
    let index = 0;
    while (offset < buf.byteLength) {
      const docsize = view.getInt32(offset, true);
      if (docsize > buf.byteLength - offset) { break; }

      offset = deserializeStream(buf, offset, 1, docs, index,
        { promoteBuffers: true, promoteValues: true });
      index++;
    }
    return docs;
  }

  async *getBsonStream<T extends Document, S extends Record<string, string | string[] | undefined>>(
    path: string, params?: S, method?: string): AsyncGenerator<T, void, void> {
       //use protocol relative if not specified
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }
    if (params) {
      path = path + "?" + this.buildQueryParams(params);
    }

    const headers = new Headers({ "X-Requested-With": "XMLHttpRequest" });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }
    const response = await fetch(path, {
      method: method || "GET",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers
    });

    if (!response.ok) {
      const body = await response.json();
      console.error("Couldn't fetch:", body, response);
      throw errorFor(body, response.status);
    }
    if (!response.body) {
      console.error("Missing body:", response);
      throw new HttpError("No response from server", response.status);
    }

    const reader = response.body.getReader();

    const docs: Array<T> = [];
    let buf: Uint8Array | null = null;
    let done: boolean = false;
    let value: Uint8Array | undefined = undefined;
    let offset = 0;

    while (!done) {
      ({ value, done } = await reader.read());
      //if (done) { break; }
      if (!value) {
        //console.log("no value returned from stream");
        continue;
      }

      if (!buf) {
        buf = value;
      } else {
        const newbuf: Uint8Array = new Uint8Array(buf.length + value.length);
        newbuf.set(buf);
        newbuf.set(value, buf.length);
        buf = newbuf;
      }

      const view = new DataView(buf.buffer);

      while (offset < buf.byteLength - 4) {
        const docsize = view.getInt32(offset, true);
        if (docsize > buf.byteLength - offset) { break; }

        offset = deserializeStream(buf, offset, 1, docs, 0,
          { promoteBuffers: true, promoteValues: true });
        yield docs[0];
      }
      if (offset < buf.byteLength) {
        buf = buf.slice(offset);
      } else {
        buf = null;
      }
      offset = 0;
    }
  }

  buildQueryParams<S extends Record<string, string | string[] | undefined>>(params: S): string {
    const queryParams = new URLSearchParams();
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const val = params[key] || "";
        if (Array.isArray(val)) {
          for (const v of val) {
            queryParams.append(key, v);
          }
        } else {
          queryParams.append(key, val);
        }
      }
    }
    return queryParams.toString();
  }

  async post<T, S extends Partial<Record<keyof S, string | undefined>> = object>(path: string, data: T, params?: S): Promise<ArrayBuffer> {
    const headers = new Headers({
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }

    if (params) {
      const queryParams = new URLSearchParams();
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          const p = params[key];
          p && queryParams.append(key, p);
        }
      }
      path = path + "?" + queryParams.toString();
    }
    //use protocol relative if not specified
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }
    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers,
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const body = await response.json();
      console.error("Couldn't post data:", body, response);
      throw errorFor(body, response.status, "Failed Posting to Server");
    }

    return await response.arrayBuffer();
  }

  async put<T>(path: string, data: T): Promise<ArrayBuffer> {
    const headers = new Headers({
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }

    const response = await fetch(path, {
      method: "PUT",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers,
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const body = await response.json();
      console.error("Couldn't post data:", body, response);
      throw errorFor(body, response.status, "Failed Posting to Server");
    }

    return await response.arrayBuffer();
  }

  async postBuf(path: string, data: ArrayBuffer, token: string | null = null): Promise<ArrayBuffer> {
    const headers: Headers = new Headers({
      "Content-Type": "application/bson",
      "X-Requested-With": "XMLHttpRequest"
    });

    if (!token) { token = await this.tokenFactory(path); }
    if (token) { headers.append("Authorization", token); }
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }

    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers,
      body: data
    });

    if (!response.ok) {
      if (response.status === 413) {
        throw new HttpError("Total Size is Too Large", response.status);
      }
      const body = await response.json();
      console.error("Couldn't post data:", body, response);
      throw errorFor(body, response.status, "Failed Posting to Server");
    }

    return await response.arrayBuffer();
  }

  async postBufJSON<T>(path: string, data: ArrayBuffer, token: string | null = null): Promise<T> {
    const headers: Headers = new Headers({
      "Content-Type": "application/bson",
      "X-Requested-With": "XMLHttpRequest"
    });

    if (!token) { token = await this.tokenFactory(path); }
    if (token) { headers.append("Authorization", token); }
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }

    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers,
      body: data
    });
    const body = await response.json();

    if (!response.ok) {
      if (response.status === 413) {
        throw new HttpError("Total Size is Too Large", response.status);
      }
      console.error("Couldn't post data:", body, response);
      throw errorFor(body, response.status, "Failed Posting to Server");
    }

    return body;
  }

  async postFileXHR(path: string, file: File, headers: { [name: string]: string } | null,
    progress: (uploaded: number, total: number) => void,
    xhrHook: (xhr: XMLHttpRequest) => void): Promise<Uint8Array> {
    const token = await this.tokenFactory(path);
    return await new bluebird<Uint8Array>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", path, true);
      xhr.responseType = "arraybuffer";

      if (token) {
        xhr.setRequestHeader("Authorization", token);
      }

      if (headers) {
        for (const name in headers) {
          xhr.setRequestHeader(name, headers[name]);
        }
      }
      xhr.addEventListener("error", err => reject(err));
      xhr.addEventListener("abort", () => reject(new AbortError("Aborted")));
      xhr.addEventListener("load", () => {
        const resp = xhr.response;
        if (xhr.status >= 400) {
          try {
            const err = JSON.parse(new TextDecoder("utf-8").decode(resp));
            return reject(errorFor(err, xhr.status, xhr.statusText));
          } catch (err) {
            console.log("Failed decoding resp:", err);
          }
          return reject(new HttpError(xhr.statusText, xhr.status));
        }

        resolve(new Uint8Array(resp));
      });
      xhrHook && xhrHook(xhr);

      if (xhr.upload) {
        xhr.upload.addEventListener("progress", evt => {
          console.log("upload progress:", evt.loaded, evt.total, evt);
          if (evt.lengthComputable) {
            progress(evt.loaded, evt.total);
          }
        });
      }
      xhr.send(file);
    });
  }

  async postFile(path: string, data: File, abortSignal: AbortSignal): Promise<any> {
    const headers: Headers = new Headers({
      "Content-Type": "application/bson",
      "X-Requested-With": "XMLHttpRequest"
    });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }

    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers,
      body: data,
      signal: abortSignal
    });

    const body = await response.json();

    if (!response.ok) {
      console.error("Couldn't post data:", body, response);
      throw errorFor(body, response.status, "Failed Posting to Server");
    }

    return await response.json();
  }

  setTokenFactory(factory: (path: string) => Promise<string | null>): void {
    this.tokenFactory = factory;
  }
}



export function binaryHttpTestFactory(): BinaryHTTPService {
  return new BinaryHTTPService();
}


export const http = new BinaryHTTPService();

